<template>
  <BaseModule
    v-bind="$props"
    class="testimonials-module"
    data-module="testimonials"
    :style="{ backgroundColor: testimonialsPrimaryColor }"
  >
    <header
      v-if="title"
      class="base-title-fontsize"
    >
      <component
        :is="titleTag"
        class="tit tit-g"
      >
        {{ title }}
      </component>
    </header>

    <Warning
      v-if="testimonials.length === 0 && editing"
      text="O módulo não possui nenhum depoimento adicionado"
    >
    </Warning>

    <!-- Slider main container -->
    <div
      ref="swiper"
      class="swiper"
    >
      <div class="quote-container">
        <div
          class="quote i-quote-open"
          :style="{ color: testimonialsSecondaryColor }"
        >
          “
        </div>
        <div
          class="quote i-quote-close"
          :style="{ color: testimonialsSecondaryColor }"
        >
          ”
        </div>
      </div>

      <!-- Additional required wrapper -->
      <div class="swiper-wrapper">
        <!-- Slides -->
        <div
          v-for="(testimonial, idx) in testimonials"
          :key="testimonial.id || idx"
          class="swiper-slide"
        >
          <blockquote>
            <div
              class="quote i-quote-center"
              :style="{ color: testimonialsSecondaryColor }"
            >
              “
            </div>
            <div
              class="txt"
              :style="{ color: testimonialColor }"
              v-html="testimonial.testimonial"
            ></div>
            <div class="img-author base-content-fontsize">
              <LImage
                v-if="hasAuthorImage(testimonial)"
                :src="testimonial.image"
                :alt="testimonial.author"
                loader-size="32"
              />
              <p
                class="author"
                :style="{ color: testimonialsSecondaryColor }"
              >
                {{ testimonial.author }}
              </p>
            </div>
          </blockquote>
        </div>
      </div>

      <!-- If we need navigation buttons -->
      <div
        v-if="hasMultipleTestimonials"
        :class="`swiper-button-prev btn-prev-${instanceId}`"
      ></div>
      <div
        v-if="hasMultipleTestimonials"
        :class="`swiper-button-next btn-next-${instanceId}`"
      ></div>

      <!-- Add Pagination -->
      <div
        ref="pagination"
        class="swiper-pagination"
      ></div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState } from 'vuex';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

import base from '@/mixins/base';
import Warning from '@/components/Warning.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'Testimonials',
  components: {
    BaseModule,
    Warning,
  },
  mixins: [base],
  props: {
    ...BaseModule.props,
    testimonialsPrimaryColor: {
      type: String,
      default: '',
    },
    testimonialsSecondaryColor: {
      type: String,
      default: '',
    },
    testimonialColor: {
      type: String,
      default: '',
    },
    testimonials: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      swiper: null,
    };
  },
  computed: {
    editing() {
      return typeof this.$parent === 'undefined';
    },
    hasMultipleTestimonials() {
      return this.testimonials.length > 1;
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
  },
  watch: {
    currentEditableItem() {
      this.slideToActivatedItem();
    },
  },
  mounted() {
    this.swiper = new Swiper(this.$refs.swiper, {
      modules: [Autoplay, Navigation, Pagination],
      observer: true,
      speed: 500,
      allowTouchMove: false,
      pagination: {
        el: this.$refs.pagination,
        type: 'bullets',
        clickable: true,
      },
      navigation: {
        nextEl: `.btn-next-${this.instanceId}`,
        prevEl: `.btn-prev-${this.instanceId}`,
      },
    });
  },
  methods: {
    hasAuthorImage(testimonial) {
      return (
        typeof testimonial.image === 'string' && /\S/.test(testimonial.image)
      );
    },
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null
      ) {
        if (this.currentEditableItem.includes('testimonials')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );
          if (this.swiper && itemPathIndex < this.swiper.slides.length) {
            this.swiper.slideTo(itemPathIndex);
          }
        }
      }
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            toRemove: ['subtitle'],
          },
          {
            name: 'itens',
            label: 'Itens',
            fields: [
              {
                name: 'testimonials',
                type: 'List',
                minItems: 1,
                itemLabel: 'Depoimento {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar depoimento',
                fields: [
                  {
                    name: 'testimonial',
                    type: 'Html',
                    label: 'Texto',
                  },
                  {
                    name: 'author',
                    type: 'ShortText',
                    label: 'Autor',
                    maxlength: 70,
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem do autor',
                    helpText: 'Dimensões recomendadas: 50x50',
                    enableRemoval: true,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'testimonialColor',
                type: 'ColorPicker',
                label: 'Cor dos depoimentos',
              },
              {
                name: 'testimonialsPrimaryColor',
                type: 'ColorPicker',
                label: 'Cor do fundo',
              },
              {
                name: 'testimonialsSecondaryColor',
                type: 'ColorPicker',
                label: 'Cor dos detalhes',
              },
            ],
            toRemove: [
              'backgroundColor',
              'textColor',
              'buttonColor',
              'buttonTextColor',
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/libs/swiper';

.swiper {
  width: 100%;

  .swiper-wrapper {
    align-items: center;
  }

  .swiper-button-prev,
  .swiper-button-next {
    width: 60px;
    height: 60px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 40px;

    &::after {
      font-size: 30px;
      color: $lb-white;
    }
  }
}

.testimonials-module {
  padding: 0;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  position: relative;

  .tit {
    text-align: center;
  }

  .swiper-slide {
    line-height: 1.8em;
    margin: 0 auto;
    position: static;
    text-align: center;
    width: 100% !important;

    blockquote {
      width: 100%;
      height: 100%;
      margin: 0 0 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .txt {
      max-width: 550px;
      margin: auto;
      font-weight: $lb-normal;

      &::v-deep {
        p {
          font-size: 1em;
          line-height: 1.3;
        }
      }
    }

    .img-author {
      padding-top: 30px;

      .img {
        width: 50px;
        height: 50px;
        border-radius: 40px;
        margin: auto;
      }
    }

    .author {
      margin-top: 1.5em;
      font-weight: $lb-semibold;
      font-size: 0.87em;
    }
  }

  .quote-container {
    position: absolute;
    height: 100%;
    width: 100%;
    margin-top: -40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .quote {
    font-size: 120px;

    &.i-quote-open {
      margin-left: calc(50% - 380px);
    }
    &.i-quote-close {
      margin-right: calc(50% - 380px);
    }
    &.i-quote-center {
      position: relative;
      top: 0%;
      display: none;
    }
  }

  .swiper-horizontal > .swiper-pagination-bullets {
    padding-top: 20px;
    position: initial;
  }

  &::v-deep {
    .swiper-pagination-bullet,
    .swiper-pagination-bullet-active {
      background: var(--title-color, $lb-dark-gray) !important;
    }
  }
}

@include media-breakpoint-down(md) {
  .testimonials-module {
    .quote {
      z-index: 1;
      top: unset;
      font-size: 96px;

      &.i-quote-open {
        display: none;
      }
      &.i-quote-close {
        display: none;
      }
      &.i-quote-center {
        display: block;
      }
    }
  }
}

@include media-breakpoint-down(sm) {
  .testimonials-module {
    padding: 0 6.25vw;
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);

    .swiper-slide {
      width: 100%;

      .txt {
        margin: 0 25px;
      }
    }
  }

  .swiper {
    .swiper-button-prev,
    .swiper-button-next {
      top: unset;
      bottom: 30px;
    }

    .swiper-button-prev {
      left: 33%;
    }

    .swiper-button-next {
      right: 33%;
    }
    .swiper-pagination {
      padding-top: 50px;
    }
  }
}

@include media-breakpoint-down(sm) {
  .swiper {
    .swiper-button-prev,
    .swiper-button-next {
      width: 40px;
      height: 40px;

      &::after {
        font-size: 20px;
      }
    }
  }

  .swiper-slide {
    .author {
      margin-bottom: 25px;
    }

    .img-author {
      padding-top: 20px !important;
    }
  }
}
</style>
